import { createRouter, createWebHistory } from 'vue-router';
import type { Router } from 'vue-router';
import { ElMessage } from 'element-plus';
import AppLayout from '@/app/layouts/AppLayout.vue';
import { useAuthStore } from '@/entities/auth';
import { i18n } from '@/shared/i18n';
import { getConfig } from '@/shared/config/runtime';

/**
 * Создаёт router с правильным base path из конфигурации
 * ВАЖНО: Вызывать ПОСЛЕ loadConfig()!
 */
export function createAppRouter(): Router {
  // Получаем base path из загруженного конфига
  const config = getConfig();
  let basePath = '/';
  
  if (config.basePath !== undefined && config.basePath.length > 0) {
    // Убедимся что путь начинается с / и заканчивается на /
    basePath = config.basePath;
    if (!basePath.startsWith('/')) basePath = `/${basePath}`;
    if (!basePath.endsWith('/')) basePath = `${basePath}/`;
  }

  const router = createRouter({
    history: createWebHistory(basePath),
    routes: [
    // Auth routes (public)
    {
      path: '/login',
      name: 'login',
      component: (): Promise<typeof import('@/pages/auth/LoginPage.vue')> =>
        import('@/pages/auth/LoginPage.vue'),
      meta: { public: true },
    },
    {
      path: '/change-password',
      name: 'change-password',
      component: (): Promise<typeof import('@/pages/auth/ChangePasswordPage.vue')> =>
        import('@/pages/auth/ChangePasswordPage.vue'),
      meta: { requiresAuth: true, allowPasswordChange: true },
    },
    
    // Protected routes
    //
    // Проектные разделы помечены `projectScoped`: их содержимое зависит от
    // проекта, поэтому проект входит в адрес и восстанавливается по ссылке
    // (app-shell-shell-routing-R12). Разделы, работающие ещё и в контексте
    // фичи, помечены `featureScoped`: фича в адресе необязательна, но
    // выбранная в нём отражается. Разбор и поддержание адреса — providers/
    // project-context.ts.
    {
      path: '/',
      component: AppLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'source-explorer',
          component: (): Promise<
            typeof import('@/pages/source-explorer/SourceExplorerPage.vue')
          > => import('@/pages/source-explorer/SourceExplorerPage.vue'),
          meta: { projectScoped: true },
        },
        {
          path: '/business-model',
          name: 'business-model',
          component: (): Promise<typeof import('@/pages/business-model/BusinessModelPage.vue')> =>
            import('@/pages/business-model/BusinessModelPage.vue'),
          meta: { projectScoped: true, featureScoped: true },
        },
        {
          path: '/mapping',
          name: 'mapping',
          component: (): Promise<typeof import('@/pages/mapping/MappingPage.vue')> =>
            import('@/pages/mapping/MappingPage.vue'),
          meta: { projectScoped: true, featureScoped: true },
        },
        {
          path: '/datamarts',
          name: 'datamarts',
          component: (): Promise<typeof import('@/pages/datamarts/DatamartsPage.vue')> =>
            import('@/pages/datamarts/DatamartsPage.vue'),
          meta: { projectScoped: true, featureScoped: true },
        },
        {
          // Раздел ETL не построен: прямой заход на его путь ведёт в домашний
          // раздел (shell-routing-R08). Прежде путь показывал дерево
          // источников под чужим адресом, и ни один пункт навигации не был
          // подсвечен — адрес расходился с содержимым.
          path: '/etl',
          redirect: '/',
        },
        {
          // История изменений — раздел оболочки наравне с бизнес-моделью,
          // маппингом и витринами (change-history-R10, shell-routing-R04).
          // Второй вход — из карточки объекта: тот же раздел с
          // предустановленным фильтром (R09).
          path: '/change-history',
          name: 'change-history',
          component: (): Promise<typeof import('@/pages/change-history/ChangeHistoryPage.vue')> =>
            import('@/pages/change-history/ChangeHistoryPage.vue'),
          meta: { projectScoped: true },
          props: (route): { entityKind?: string; objectId?: string } => ({
            entityKind: typeof route.query['entityKind'] === 'string' ? route.query['entityKind'] : undefined,
            objectId: typeof route.query['objectId'] === 'string' ? route.query['objectId'] : undefined,
          }),
        },
        {
          path: '/admin/users',
          name: 'admin-users',
          component: (): Promise<typeof import('@/pages/admin/UsersPage.vue')> =>
            import('@/pages/admin/UsersPage.vue'),
          meta: { requiresAuth: true, requiresAdmin: true },
        },
      ],
    },
  ],
  });

  // Router guards
  router.beforeEach(async (to, _from, next) => {
    const authStore = useAuthStore();
    const isPublicRoute = to.meta['public'] === true;
    const requiresAuth = to.meta['requiresAuth'] === true;

    // Initialize auth if not yet initialized
    if (authStore.isAuthenticated === false && isPublicRoute === false) {
      const initialized = await authStore.initializeAuth();
      if (initialized === false && requiresAuth === true) {
        next({ name: 'login', query: { redirect: to.fullPath } });
        return;
      }
    }

    // Redirect to home if already authenticated and trying to access login
    if (authStore.isAuthenticated === true && to.name === 'login') {
      next({ path: '/' });
      return;
    }

    // Check if password change is required
    if (authStore.mustChangePassword === true && to.name !== 'change-password' && to.meta['allowPasswordChange'] !== true) {
      next({ name: 'change-password' });
      return;
    }

    // Check authentication requirement
    if (requiresAuth === true && authStore.isAuthenticated === false) {
      next({ name: 'login', query: { redirect: to.fullPath } });
      return;
    }

    // Раздел требует прав администратора, а их нет: отказ **явный** —
    // локализованное сообщение об отказе в доступе, после чего пользователь
    // переводится на главную (access-and-auth-login-sessions-R12, основание:
    // вердикт 247). Скрывать существование раздела не требуется: состав
    // разделов оболочки к защищаемым данным не относится, в отличие от
    // объектов проекта (rbac-R23).
    if (to.meta['requiresAdmin'] === true && authStore.isAdmin === false) {
      ElMessage.error(i18n.global.t('auth.sectionForbidden'));
      next({ path: '/' });
      return;
    }

    next();
  });

  return router;
}

